import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  CircularProgress,
  List,
  ListItemButton,
  Paper,
  Popper,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import SearchIconAsset from 'assets/icons/search.svg';
import { useGetSearchValuesQuery } from 'store/app/entities/EmployeesApiSlice';
import authCompany from 'store/selectors/authCompany';
import { I18n } from 'utils/i18n';
import { getFieldFromKeyword, getLocalizedKeyword, searchFields } from 'utils/searchKeywords';
import { buildSearchStringFromChips, parseSearchStringToChips } from 'utils/searchQueryBuilder';
import { shouldRehydrateSearch } from './searchSync';

const i18nKeys = {
  team: 'teams',
  name: 'name',
  department: 'department',
  code: 'code',
  jobTitle: 'jobTitle',
  location: 'location',
  reportsTo: 'reportsTo',
  division: 'division',
  tags: 'tags',
};

const VALUE_SUGGESTIONS_PAGE_SIZE = 50;

const getStyles = theme => ({
  wrapper: {
    flexGrow: 1,
    display: 'flex',
    minWidth: 0,
  },
  root: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '4px',
    minHeight: 40,
    padding: theme.spacing(0.5, 1),
    borderRadius: 1,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
      outline: `1px solid ${theme.palette.primary.main}`,
    },
    flexGrow: 1,
  },
  searchIcon: {
    fontSize: 20,
    color: theme.palette.secondary.main,
    marginRight: '4px',
  },
  inputWrapper: {
    flex: 1,
    minWidth: 80,
    display: 'flex',
    alignItems: 'center',
  },
  input: {
    '& .MuiInputBase-input': {
      padding: theme.spacing(0.5, 0),
      fontSize: 14,
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  pendingPill: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px dashed',
    borderColor: theme.palette.divider,
    borderRadius: '4px',
    padding: '1px 4px',
    marginRight: '2px',
  },
  pillInput: {
    flex: 1,
    minWidth: 8,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    lineHeight: 'inherit',
    color: 'inherit',
    padding: 0,
  },
  // Plain-text chip (no field binding) — simpler, no field button
  textPill: {
    display: 'inline-flex',
    alignItems: 'stretch',
    flexShrink: 0,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '4px',
    overflow: 'hidden',
    backgroundColor: theme.palette.secondary.ultraLight,
    '&:hover .chip-delete': {
      width: 20,
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
      outline: `1px solid ${theme.palette.primary.main}`,
    },
  },
  // Completed chip — Apple Mail style
  completedPill: {
    display: 'inline-flex',
    alignItems: 'stretch',
    flexShrink: 0,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '4px',
    overflow: 'hidden',
    backgroundColor: theme.palette.secondary.ultraLight,
    '&:hover .chip-delete': {
      width: 20,
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
      outline: `1px solid ${theme.palette.primary.main}`,
    },
  },
  // Left segment: field name + caret
  chipKey: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    padding: '2px 4px 2px 6px',
    backgroundColor: theme.palette.action.subtle,
    borderRight: `1px solid ${theme.palette.divider}`,
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    lineHeight: 'inherit',
    color: 'inherit',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    '&:hover': {
      backgroundColor: theme.palette.action.subtleHover,
    },
  },
  // Right segment: editable value input
  chipValueInput: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    lineHeight: 'inherit',
    color: 'inherit',
    padding: '2px 2px 2px 6px',
    minWidth: 20,
    flex: 1,
  },
  // Delete × button — collapses to zero width when chip is not hovered
  chipDelete: {
    width: 0,
    minWidth: 0,
    overflow: 'hidden',
    transition: 'width 0.15s ease',
    display: 'flex',
    alignItems: 'center',
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    color: theme.palette.text.secondary,
    flexShrink: 0,
    '&:hover': {
      backgroundColor: theme.palette.action.subtleMedium,
      color: theme.palette.text.primary,
    },
  },
  // Field selector dropdown (per-chip)
  fieldSelector: {
    minWidth: 160,
    maxHeight: 300,
    overflow: 'auto',
    borderRadius: '6px',
    boxShadow: theme.shadows[4],
  },
  popover: {
    width: 380,
    maxHeight: 420,
    backgroundColor: theme.palette.background.paper,
    borderRadius: '8px',
    overflow: 'hidden',
  },
  popoverList: {
    maxHeight: 240,
    overflow: 'auto',
    padding: theme.spacing(0.5, 0),
  },
  popoverListNoSuggestions: {
    padding: 0,
  },
  popoverItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: theme.spacing(1.25, 2),
    gap: theme.spacing(2),
    '&:hover': {
      backgroundColor: theme.palette.action.subtleLight,
    },
  },
  popoverItemMain: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 0,
    color: theme.palette.text.primary,
  },
  popoverItemSecondary: {
    flexShrink: 0,
    fontSize: 12,
    color: theme.palette.text.disabled,
  },
  popoverSyntax: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.background.subtle,
    borderTop: `1px solid ${theme.palette.action.subtleMedium}`,
  },
  popoverSyntaxNoSuggestions: {
    borderTop: 'none',
  },
  valueSuggestionsTruncated: {
    padding: theme.spacing(1, 2),
    borderTop: `1px solid ${theme.palette.action.subtleMedium}`,
    backgroundColor: theme.palette.background.subtle,
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  popoverSyntaxExamples: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
  syntaxExample: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    '& code': {
      padding: '2px 6px',
      borderRadius: '4px',
      backgroundColor: theme.palette.action.subtleMedium,
      fontSize: 12,
      marginLeft: '4px',
    },
  },
});

// Measures actual text width (so all-caps and narrow chars both fit) and sets input minWidth
const ChipValueInput = React.forwardRef(({ value, sx, style, ...rest }, ref) => {
  const sizerRef = useRef(null);
  const [minWidth, setMinWidth] = useState(20);

  useLayoutEffect(() => {
    if (!sizerRef.current) return;
    const w = sizerRef.current.scrollWidth;
    setMinWidth(Math.max(20, w + 4));
  }, [value]);

  return (
    <Box
      component="span"
      sx={{ position: 'relative', display: 'inline-flex', flex: 1, minWidth: 20 }}
    >
      <Box
        ref={sizerRef}
        component="span"
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          whiteSpace: 'pre',
          fontFamily: 'inherit',
          fontSize: 'inherit',
          lineHeight: 'inherit',
          padding: '2px 2px 2px 6px',
          left: 0,
          top: 0,
        }}
      >
        {value || ' '}
      </Box>
      <Box
        component="input"
        ref={ref}
        sx={sx}
        value={value}
        size={Math.max(1, value?.length || 1)}
        style={{ ...style, minWidth: `${minWidth}px` }}
        {...rest}
      />
    </Box>
  );
});

const PeopleSearchBar = ({ value = '', onChange, placeholder = null }) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const locale = useSelector(state => state.i18n.locale);
  const company = useSelector(authCompany);
  const companyId = company?._id;
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const listRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const ignoreNextIntersectionRef = useRef(false);
  const atBottomTriggeredRef = useRef(false);
  // Refs keyed by chip index for anchoring the field-selector popper
  const chipKeyRefs = useRef({});

  const [chips, setChips] = useState([]);
  const [pendingField, setPendingField] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  // Index of the chip whose field-selector dropdown is open (null = none)
  const [fieldSelectorIndex, setFieldSelectorIndex] = useState(null);
  // Index of the chip whose value input is focused (for showing value suggestions in popover)
  const [focusedChipIndex, setFocusedChipIndex] = useState(null);
  const syncingFromProp = useRef(false);
  // Tracks the last string we emitted so the sync-from-prop effect can
  // distinguish our own echoed-back value from an external prop change.
  const lastEmittedRef = useRef(value);
  const valuePartRawRef = useRef('');
  // Chip-value inline editing helpers
  const isEditingChipValueRef = useRef(false);
  const chipValueBeforeEditRef = useRef(null);
  const escapeChipValueRef = useRef(false);
  // When user selects a value from the popover while editing a chip, skip blur's setChips so we don't overwrite
  const didSelectSuggestionRef = useRef(false);
  // Pagination for value suggestions: skip we requested; merge only when we get a new page
  const lastMergedSkipRef = useRef(-1);

  const [valuesSkip, setValuesSkip] = useState(0);
  const [accumulatedValues, setAccumulatedValues] = useState([]);

  // ─── Sync from value prop ────────────────────────────────────────────────
  useEffect(() => {
    const parsed = parseSearchStringToChips(value, locale);
    const built = buildSearchStringFromChips(parsed, locale);
    const chipString = buildSearchStringFromChips(chips, locale);
    const shown = inputValue
      ? chipString
        ? `${chipString} ${inputValue}`
        : inputValue
      : chipString;
    if (!shouldRehydrateSearch({ value, lastEmitted: lastEmittedRef.current, shown })) return;
    if (built !== chipString || !value) {
      syncingFromProp.current = true;
      setChips(parsed);
      setInputValue('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync chips from external value only when value/locale changes
  }, [value, locale]);

  useEffect(() => {
    if (syncingFromProp.current) {
      syncingFromProp.current = false;
      return;
    }
    if (pendingField || isEditingChipValueRef.current) return;
    const base = buildSearchStringFromChips(chips, locale);
    const full = inputValue.trim() ? (base ? `${base} ${inputValue}` : inputValue) : base;
    if (full !== value) {
      lastEmittedRef.current = full;
      onChange?.({ target: { value: full } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- emit derived search string without re-running on parent value/onChange identity
  }, [chips, inputValue, pendingField, locale]);

  // ─── Derived values for the free-text / pending-pill input ──────────────
  // When editing a chip, show value suggestions for that chip's field
  const suggestionsField =
    pendingField ||
    (focusedChipIndex !== null && chips[focusedChipIndex]?.field
      ? chips[focusedChipIndex].field
      : null);
  const phase = suggestionsField ? 'values' : 'fields';

  // When editing a field-less chip, filter the field list by the chip's value
  const fieldFilterInput =
    focusedChipIndex !== null && chips[focusedChipIndex]?.field == null
      ? (chips[focusedChipIndex]?.value ?? '')
      : inputValue;

  const valuePartRaw =
    pendingField && inputValue.includes(':')
      ? inputValue.slice(inputValue.indexOf(':') + 1)
      : focusedChipIndex !== null
        ? (chips[focusedChipIndex]?.value ?? '')
        : inputValue;
  const valuePart = valuePartRaw.trim();
  const valuePartUnquoted = valuePart.replace(/^"(.*)"$/, '$1').replace(/^"/, '');
  const valuePartNormalized = valuePartUnquoted.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  valuePartRawRef.current = valuePartRaw;

  // Reset value-suggestions pagination when field or search query changes
  useEffect(() => {
    if (!suggestionsField) return;
    setValuesSkip(0);
    setAccumulatedValues([]);
    lastMergedSkipRef.current = -1;
    atBottomTriggeredRef.current = false;
  }, [suggestionsField, valuePartNormalized]);

  const {
    data: valueResponse,
    isLoading: valuesLoading,
    isFetching: valuesFetching,
  } = useGetSearchValuesQuery(
    {
      companyId,
      field: suggestionsField,
      q: valuePartNormalized,
      skip: valuesSkip,
      limit: VALUE_SUGGESTIONS_PAGE_SIZE,
    },
    { skip: !companyId || !suggestionsField },
  );

  const valueOptions = useMemo(
    () => (Array.isArray(valueResponse?.values) ? valueResponse.values : []),
    [valueResponse],
  );
  const hasMoreValues = valueResponse?.hasMore ?? false;

  // Merge paginated value results: first page replaces, next pages append (avoid duplicate merge)
  useEffect(() => {
    if (!Array.isArray(valueOptions)) return;
    if (valuesSkip === 0) {
      setAccumulatedValues(valueOptions);
      lastMergedSkipRef.current = 0;
    } else if (valueOptions.length > 0 && valuesSkip > lastMergedSkipRef.current) {
      setAccumulatedValues(prev => [...prev, ...valueOptions]);
      lastMergedSkipRef.current = valuesSkip;
    }
  }, [valueOptions, valuesSkip]);

  const filteredFields = useMemo(() => {
    if (!fieldFilterInput.trim()) return searchFields;
    const lower = fieldFilterInput.toLowerCase();
    return searchFields.filter(f => {
      const kw = getLocalizedKeyword(f, locale);
      return kw.toLowerCase().includes(lower);
    });
  }, [fieldFilterInput, locale]);

  const filteredValues = useMemo(() => {
    if (!Array.isArray(accumulatedValues)) return [];
    return accumulatedValues;
  }, [accumulatedValues]);

  const isLoadingMore = valuesFetching && valuesSkip > 0;

  // Reliable "load more" when user scrolls to bottom of values list (onScroll fallback)
  const handleValuesListScroll = useCallback(
    e => {
      if (!hasMoreValues || isLoadingMore) return;
      const el = e.target;
      if (!el || el.nodeType !== 1) return;
      const { scrollTop, clientHeight, scrollHeight } = el;
      const threshold = 40;
      const atBottom = scrollTop + clientHeight >= scrollHeight - threshold;
      if (atBottom && !atBottomTriggeredRef.current) {
        atBottomTriggeredRef.current = true;
        setValuesSkip(prev => prev + VALUE_SUGGESTIONS_PAGE_SIZE);
      }
      if (!atBottom) atBottomTriggeredRef.current = false;
    },
    [hasMoreValues, isLoadingMore],
  );

  // When the list is not scrollable (few items) but has more to load, trigger once so we fill it
  useEffect(() => {
    if (phase !== 'values' || !hasMoreValues || isLoadingMore || atBottomTriggeredRef.current)
      return;
    const listEl = listRef.current;
    if (!listEl || filteredValues.length === 0) return;
    if (listEl.scrollHeight <= listEl.clientHeight + 20) {
      atBottomTriggeredRef.current = true;
      setValuesSkip(prev => prev + VALUE_SUGGESTIONS_PAGE_SIZE);
    }
  }, [phase, hasMoreValues, isLoadingMore, filteredValues.length]);

  // Infinite scroll: load next page when user scrolls to the end of value suggestions.
  // Ignore the first intersection callback (initial state when we connect) so we only
  // trigger when the user scrolls the sentinel into view. Root = scrollable list so
  // we trigger when the sentinel enters the list's visible area.
  useEffect(() => {
    if (phase !== 'values' || !hasMoreValues || isLoadingMore || !popoverOpen) return () => {};
    const sentinel = loadMoreSentinelRef.current;
    const listEl = listRef.current;
    if (!sentinel || !listEl) return () => {};

    ignoreNextIntersectionRef.current = true;
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry) return;
        if (!entry.isIntersecting) return;
        if (ignoreNextIntersectionRef.current) {
          ignoreNextIntersectionRef.current = false;
          return;
        }
        setValuesSkip(prev => prev + VALUE_SUGGESTIONS_PAGE_SIZE);
      },
      { root: listEl, rootMargin: '80px', threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [phase, hasMoreValues, isLoadingMore, popoverOpen]);

  const suggestionsList = phase === 'fields' ? filteredFields : filteredValues;
  const hasSuggestions =
    phase === 'fields' ? filteredFields.length > 0 : !valuesLoading && filteredValues.length > 0;

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [phase, suggestionsList, popoverOpen]);

  useEffect(() => {
    if (highlightedIndex < 0 || !popoverOpen || !listRef.current) return;
    const item = listRef.current.querySelector(`[data-suggestion-index="${highlightedIndex}"]`);
    item?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
  }, [highlightedIndex, popoverOpen]);

  // ─── Search emit ─────────────────────────────────────────────────────────
  const emitSearch = useCallback(
    newChips => {
      const str = buildSearchStringFromChips(newChips, locale);
      lastEmittedRef.current = str;
      onChange?.({ target: { value: str } });
    },
    [locale, onChange],
  );

  // ─── Pending-pill / free-text handlers ───────────────────────────────────
  const handleFieldSelect = useCallback(
    field => {
      const prefix = `${getLocalizedKeyword(field, locale)}:`;
      setPendingField(field);
      setInputValue(prefix);
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [locale],
  );

  const handleValueSelect = useCallback(
    selectedValue => {
      let val =
        typeof selectedValue === 'object'
          ? (selectedValue.label ?? selectedValue.name ?? selectedValue.value ?? '')
          : String(selectedValue);
      const userStartedQuote = valuePartRawRef.current.startsWith('"');
      if (val.includes(' ') || userStartedQuote) {
        if (val.startsWith('"') && val.endsWith('"')) {
          // already properly quoted
        } else if (val.startsWith('"')) {
          val = `${val}"`; // auto-close unbalanced opening quote
        } else {
          val = `"${val}"`;
        }
      }
      // Applying to the chip being edited
      if (focusedChipIndex !== null && chips[focusedChipIndex]?.field) {
        const newChips = chips.map((c, i) => (i === focusedChipIndex ? { ...c, value: val } : c));
        setChips(newChips);
        setFocusedChipIndex(null);
        didSelectSuggestionRef.current = true;
        emitSearch(newChips);
        return;
      }
      // Adding new chip from pending pill
      if (!pendingField) return;
      const newChips = [...chips, { field: pendingField, value: val }];
      setChips(newChips);
      setPendingField(null);
      setInputValue('');
      emitSearch(newChips);
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [chips, pendingField, focusedChipIndex, emitSearch],
  );

  const handleRemoveChip = useCallback(
    index => {
      const newChips = chips.filter((_, i) => i !== index);
      setChips(newChips);
      emitSearch(newChips);
    },
    [chips, emitSearch],
  );

  // ─── Chip field selector ─────────────────────────────────────────────────
  const handleChangeChipField = useCallback(
    (chipIndex, newField) => {
      const newChips = [...chips];
      newChips[chipIndex] = { ...newChips[chipIndex], field: newField };
      setChips(newChips);
      emitSearch(newChips);
      setFieldSelectorIndex(null);
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [chips, emitSearch],
  );

  // ─── Chip inline value editing ───────────────────────────────────────────
  const handleChipValueChange = useCallback(
    (chipIndex, newValue) => {
      isEditingChipValueRef.current = true;
      const newChips = [...chips];
      newChips[chipIndex] = { ...newChips[chipIndex], value: newValue };
      setChips(newChips);
    },
    [chips],
  );

  const handleChipValueBlur = useCallback(
    chipIndex => {
      const escape = escapeChipValueRef.current;
      escapeChipValueRef.current = false;
      isEditingChipValueRef.current = false;

      const revertTo = escape ? chipValueBeforeEditRef.current : null;
      chipValueBeforeEditRef.current = null;

      const rawVal = revertTo ?? chips[chipIndex]?.value ?? '';
      let val = rawVal.trim();

      if (!val) {
        handleRemoveChip(chipIndex);
        return;
      }
      if (val.startsWith('"') && !val.endsWith('"')) {
        val = `${val}"`;
      }

      const newChips = chips.map((c, idx) => (idx === chipIndex ? { ...c, value: val } : c));

      // Defer commit so a click on a popover suggestion can run first and set didSelectSuggestionRef
      setTimeout(() => {
        if (didSelectSuggestionRef.current) {
          didSelectSuggestionRef.current = false;
        } else {
          setChips(newChips);
          emitSearch(newChips);
        }
        if (wrapperRef.current?.contains(document.activeElement)) return;
        setPopoverOpen(false);
        setFieldSelectorIndex(null);
        setFocusedChipIndex(null);
      }, 0);
    },
    [chips, emitSearch, handleRemoveChip],
  );

  const handleChipValueKeyDown = useCallback(
    (e, chipIndex) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.target.blur();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        escapeChipValueRef.current = true;
        e.target.blur();
      } else if (e.key === 'Backspace' && e.target.value === '') {
        e.preventDefault();
        handleRemoveChip(chipIndex);
      }
    },
    [handleRemoveChip],
  );

  // ─── Focus / blur for main input ─────────────────────────────────────────
  const handleInputFocus = useCallback(() => {
    setFocusedChipIndex(null);
    setPopoverOpen(true);
    setFieldSelectorIndex(null);
  }, []);

  const handleInputBlur = useCallback(() => {
    setTimeout(() => {
      if (wrapperRef.current?.contains(document.activeElement)) return;
      setPopoverOpen(false);
      setFieldSelectorIndex(null);
    }, 0);
  }, []);

  // ─── Keyboard navigation (main input + pending pill) ─────────────────────
  const handleKeyDown = useCallback(
    e => {
      if (popoverOpen && hasSuggestions) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setHighlightedIndex(i => (i < suggestionsList.length - 1 ? i + 1 : i === -1 ? 0 : i));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setHighlightedIndex(i => (i <= 0 ? (i === -1 ? suggestionsList.length - 1 : -1) : i - 1));
          return;
        }
        if (e.key === 'Enter' && highlightedIndex >= 0) {
          e.preventDefault();
          const item = suggestionsList[highlightedIndex];
          if (phase === 'fields') {
            handleFieldSelect(item);
          } else {
            handleValueSelect(item);
          }
          setHighlightedIndex(-1);
          return;
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setPopoverOpen(false);
        setHighlightedIndex(-1);
        if (pendingField) {
          setPendingField(null);
          setInputValue('');
        }
        return;
      }
      if (e.key === 'Backspace') {
        if (pendingField) {
          const prefix = `${getLocalizedKeyword(pendingField, locale)}:`;
          if (inputValue === prefix) {
            e.preventDefault();
            setPendingField(null);
            setInputValue('');
            return;
          }
          return;
        }
        // Only remove last chip when main input is empty; not when editing a chip value
        if (focusedChipIndex === null && !inputValue && chips.length > 0) {
          e.preventDefault();
          handleRemoveChip(chips.length - 1);
          return;
        }
        return;
      }
      if (pendingField) {
        const cursor = e.target.selectionStart ?? valuePartRaw.length;
        const beforeCursor = valuePartRaw.slice(0, cursor);
        const quotesBeforeCursor = (beforeCursor.match(/"/g) || []).length;
        const isInsideOpenQuote = quotesBeforeCursor % 2 === 1;
        const hasClosingQuote =
          valuePartRaw.endsWith('"') && (valuePartRaw.match(/"/g) || []).length % 2 === 0;
        const cursorIsAfterClosingQuote = hasClosingQuote && cursor >= valuePartRaw.length;

        if (e.key === '"') {
          if (isInsideOpenQuote) {
            e.preventDefault();
            handleValueSelect(`${valuePartRaw}"`);
            return;
          }
          if (hasClosingQuote && !cursorIsAfterClosingQuote) {
            e.preventDefault();
            return;
          }
          return;
        }

        if (e.key === ' ') {
          if (cursorIsAfterClosingQuote) {
            e.preventDefault();
            handleValueSelect(valuePartRaw);
            return;
          }
          if (isInsideOpenQuote) {
            e.stopPropagation();
            return;
          }
          if (valuePart) {
            e.preventDefault();
            handleValueSelect(valuePart);
          }
          return;
        }

        if (cursorIsAfterClosingQuote && e.key.length === 1) {
          e.preventDefault();
          return;
        }

        if (e.key === 'Enter' && valuePart && highlightedIndex < 0) {
          e.preventDefault();
          handleValueSelect(valuePartRaw);
          return;
        }
        return;
      }

      // Plain text Enter or Space → commit as a field-less chip (not when editing a chip value)
      if (
        focusedChipIndex === null &&
        (e.key === 'Enter' || e.key === ' ') &&
        valuePart &&
        highlightedIndex < 0
      ) {
        e.preventDefault();
        const newChips = [...chips, { field: null, value: valuePart }];
        setChips(newChips);
        setInputValue('');
        emitSearch(newChips);
      }
    },
    [
      popoverOpen,
      hasSuggestions,
      suggestionsList,
      phase,
      highlightedIndex,
      inputValue,
      valuePart,
      valuePartRaw,
      chips,
      pendingField,
      focusedChipIndex,
      locale,
      handleRemoveChip,
      handleValueSelect,
      handleFieldSelect,
      emitSearch,
    ],
  );

  const handleInputChange = useCallback(
    e => {
      if (!popoverOpen) setPopoverOpen(true);
      const next = e.target.value;
      if (pendingField) {
        const prefix = `${getLocalizedKeyword(pendingField, locale)}:`;
        if (!next.startsWith(prefix)) {
          if (next.length < prefix.length) {
            setPendingField(null);
            setInputValue(next || '');
            return;
          }
          setInputValue(prefix + next.slice(prefix.length));
          return;
        }
      } else {
        const match = next.match(/^(\S+?):(.*)$/);
        if (match) {
          const [, keyword, vp] = match;
          const field = getFieldFromKeyword(keyword);
          if (field) {
            setPendingField(field);
            setInputValue(`${getLocalizedKeyword(field, locale)}:${vp}`);
            return;
          }
        }
      }
      setInputValue(next);
    },
    [pendingField, locale, popoverOpen],
  );

  const handlePaste = useCallback(
    e => {
      const pasted = e.clipboardData?.getData('text') || '';
      if (pendingField) {
        // In pending pill: insert pasted text into the value part and keep the field
        e.preventDefault();
        const prefix = `${getLocalizedKeyword(pendingField, locale)}:`;
        const input = inputRef.current;
        const currentValuePart = input ? input.value : '';
        let newValuePart;
        if (input && typeof input.selectionStart === 'number') {
          const start = input.selectionStart;
          const end = input.selectionEnd ?? start;
          newValuePart = currentValuePart.slice(0, start) + pasted + currentValuePart.slice(end);
        } else {
          newValuePart = currentValuePart + pasted;
        }
        setInputValue(prefix + newValuePart);
        return;
      }
      const parsed = parseSearchStringToChips(pasted, locale);
      if (parsed.length > 0) {
        e.preventDefault();
        const newChips = [...chips, ...parsed];
        setChips(newChips);
        setInputValue('');
        setPendingField(null);
        emitSearch(newChips);
      }
    },
    [chips, locale, emitSearch, pendingField],
  );

  return (
    <Box ref={wrapperRef} sx={styles.wrapper}>
      <Box ref={containerRef} sx={styles.root} onClick={() => inputRef.current?.focus()}>
        <SvgIcon sx={styles.searchIcon} component={SearchIconAsset} inheritViewBox />

        {/* Completed chips */}
        {chips.map((chip, i) =>
          chip.field === null ? (
            // Plain-text chip — no field selector, just value + delete
            <Box key={i} component="span" sx={styles.textPill} onClick={e => e.stopPropagation()}>
              <ChipValueInput
                sx={styles.chipValueInput}
                value={chip.value}
                onChange={e => handleChipValueChange(i, e.target.value)}
                onKeyDown={e => {
                  handleKeyDown(e);
                  handleChipValueKeyDown(e, i);
                }}
                onFocus={() => {
                  chipValueBeforeEditRef.current = chip.value;
                  setPendingField(null);
                  setInputValue('');
                  setFocusedChipIndex(i);
                  setPopoverOpen(true);
                  setFieldSelectorIndex(null);
                }}
                onBlur={() => handleChipValueBlur(i)}
                autoComplete="off"
              />
              <Box
                component="button"
                type="button"
                className="chip-delete"
                sx={styles.chipDelete}
                onClick={e => {
                  e.stopPropagation();
                  handleRemoveChip(i);
                }}
              >
                <ClearIcon sx={{ fontSize: 14 }} />
              </Box>
            </Box>
          ) : (
            // Field-bound chip — Apple Mail style
            <Box
              key={i}
              component="span"
              sx={styles.completedPill}
              onClick={e => e.stopPropagation()}
            >
              {/* Field selector button */}
              <Box
                component="button"
                ref={el => {
                  chipKeyRefs.current[i] = el;
                }}
                sx={styles.chipKey}
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  setFieldSelectorIndex(prev => (prev === i ? null : i));
                  setPopoverOpen(false);
                }}
                onBlur={() => {
                  setTimeout(() => {
                    if (wrapperRef.current?.contains(document.activeElement)) return;
                    setFieldSelectorIndex(null);
                  }, 0);
                }}
              >
                {getLocalizedKeyword(chip.field, locale)}
                <KeyboardArrowDownIcon sx={{ fontSize: 12 }} />
              </Box>

              {/* Value inline input */}
              <ChipValueInput
                sx={styles.chipValueInput}
                value={chip.value}
                onChange={e => handleChipValueChange(i, e.target.value)}
                onKeyDown={e => {
                  handleKeyDown(e);
                  handleChipValueKeyDown(e, i);
                }}
                onFocus={() => {
                  chipValueBeforeEditRef.current = chip.value;
                  setPendingField(null);
                  setInputValue('');
                  setFocusedChipIndex(i);
                  setPopoverOpen(true);
                  setFieldSelectorIndex(null);
                }}
                onBlur={() => handleChipValueBlur(i)}
                autoComplete="off"
              />

              {/* Delete button */}
              <Box
                component="button"
                type="button"
                className="chip-delete"
                sx={styles.chipDelete}
                onClick={e => {
                  e.stopPropagation();
                  handleRemoveChip(i);
                }}
              >
                <ClearIcon sx={{ fontSize: 14 }} />
              </Box>
            </Box>
          ),
        )}

        {/* Pending pill (new chip being typed) */}
        {pendingField ? (
          <Box component="span" sx={styles.pendingPill}>
            {getLocalizedKeyword(pendingField, locale)}:&#x2060;
            <Box
              component="input"
              ref={inputRef}
              type="text"
              sx={styles.pillInput}
              value={valuePartRaw}
              size={Math.max(1, valuePartRaw.length || 1)}
              onChange={e => {
                const prefix = `${getLocalizedKeyword(pendingField, locale)}:`;
                setInputValue(prefix + e.target.value);
              }}
              onKeyDown={handleKeyDown}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onPaste={handlePaste}
              placeholder=""
              autoComplete="off"
              autoFocus
            />
          </Box>
        ) : (
          <Box sx={styles.inputWrapper}>
            <TextField
              inputRef={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onPaste={handlePaste}
              placeholder={
                placeholder || I18n.t('SearchField.search', { defaultValue: 'Search people...' })
              }
              variant="outlined"
              size="small"
              fullWidth
              sx={styles.input}
              autoComplete="off"
              slotProps={{
                input: {
                  disableUnderline: true,
                  notched: false,
                },
              }}
            />
          </Box>
        )}

        {/* Main suggestions popover (fields + values) */}
        <Popper
          open={popoverOpen}
          anchorEl={containerRef.current}
          placement="bottom-start"
          disablePortal
          modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
          style={{ zIndex: 1300 }}
        >
          <Paper sx={styles.popover} onMouseLeave={() => setHighlightedIndex(-1)}>
            {phase === 'fields' && (
              <List
                ref={listRef}
                sx={{
                  ...styles.popoverList,
                  ...(!hasSuggestions ? styles.popoverListNoSuggestions : {}),
                }}
                disablePadding
              >
                {filteredFields.map((field, idx) => (
                  <ListItemButton
                    key={field}
                    data-suggestion-index={idx}
                    onClick={() => handleFieldSelect(field)}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    sx={styles.popoverItem}
                    disableGutters
                    selected={highlightedIndex === idx}
                  >
                    <Box sx={styles.popoverItemMain}>
                      <SearchIcon sx={{ fontSize: 18, color: 'rgba(28, 43, 52, 0.5)' }} />
                      {getLocalizedKeyword(field, locale)}:
                    </Box>
                    <Typography sx={styles.popoverItemSecondary}>
                      {I18n.t(`PeoplesScene.AdvancedSearch.${i18nKeys[field]}`, {
                        defaultValue: field,
                      })}
                    </Typography>
                  </ListItemButton>
                ))}
              </List>
            )}
            {phase === 'values' && (
              <List
                ref={listRef}
                sx={{
                  ...styles.popoverList,
                  ...(!hasSuggestions ? styles.popoverListNoSuggestions : {}),
                }}
                disablePadding
                onScroll={handleValuesListScroll}
              >
                {valuesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : filteredValues.length === 0 ? (
                  <Box sx={{ py: 3, px: 2, color: 'rgba(28, 43, 52, 0.55)', fontSize: 14 }}>
                    {I18n.t('PeoplesScene.AdvancedSearch.noResults', {
                      defaultValue: 'No suggestions. Type and press Enter to add.',
                    })}
                  </Box>
                ) : (
                  <>
                    {filteredValues.map((opt, idx) => {
                      const label =
                        typeof opt === 'object'
                          ? (opt.label ?? opt.name ?? opt.value ?? '')
                          : String(opt);
                      return (
                        <ListItemButton
                          key={idx}
                          data-suggestion-index={idx}
                          onClick={() => handleValueSelect(opt)}
                          onMouseEnter={() => setHighlightedIndex(idx)}
                          sx={styles.popoverItem}
                          disableGutters
                          selected={highlightedIndex === idx}
                        >
                          <Box sx={styles.popoverItemMain}>
                            <SearchIcon sx={{ fontSize: 18, color: 'rgba(28, 43, 52, 0.5)' }} />
                            {label}
                          </Box>
                        </ListItemButton>
                      );
                    })}
                    {hasMoreValues && (
                      <Box
                        ref={loadMoreSentinelRef}
                        sx={{
                          minHeight: 32,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          py: 1.5,
                          gap: 1,
                        }}
                      >
                        {isLoadingMore && (
                          <>
                            <CircularProgress size={18} />
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                              }}
                            >
                              {I18n.t('PeoplesScene.AdvancedSearch.loadingMore', {
                                defaultValue: 'Loading…',
                              })}
                            </Typography>
                          </>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </List>
            )}
            <Box
              sx={{
                ...styles.popoverSyntax,
                ...(!hasSuggestions ? styles.popoverSyntaxNoSuggestions : {}),
              }}
            >
              <Box sx={styles.popoverSyntaxExamples}>
                <Typography sx={styles.syntaxExample} component="span">
                  {I18n.t('PeoplesScene.AdvancedSearch.phrase', { defaultValue: 'Phrase' })}{' '}
                  <code>
                    {I18n.t('PeoplesScene.AdvancedSearch.phraseExample', {
                      defaultValue: 'key:"a b"',
                    })}
                  </code>
                </Typography>
                <Typography sx={styles.syntaxExample} component="span">
                  {I18n.t('PeoplesScene.AdvancedSearch.startsWith', {
                    defaultValue: 'Starts with',
                  })}{' '}
                  <code>
                    {I18n.t('PeoplesScene.AdvancedSearch.startsWithExample', {
                      defaultValue: 'key:"a b*"',
                    })}
                  </code>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Popper>

        {/* Per-chip field selector dropdown */}
        <Popper
          open={fieldSelectorIndex !== null}
          anchorEl={fieldSelectorIndex !== null ? chipKeyRefs.current[fieldSelectorIndex] : null}
          placement="bottom-start"
          disablePortal
          modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
          style={{ zIndex: 1400 }}
        >
          <Paper sx={styles.fieldSelector}>
            <List disablePadding sx={{ py: 0.5 }}>
              {searchFields.map(field => (
                <ListItemButton
                  key={field}
                  onClick={() => handleChangeChipField(fieldSelectorIndex, field)}
                  selected={chips[fieldSelectorIndex]?.field === field}
                  sx={{
                    px: 2,
                    py: 1,
                    fontSize: 14,
                    fontWeight: chips[fieldSelectorIndex]?.field === field ? 600 : 400,
                  }}
                >
                  {getLocalizedKeyword(field, locale)}
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Popper>
      </Box>
    </Box>
  );
};

PeopleSearchBar.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

export default PeopleSearchBar;
